const isObject = (value: unknown): value is { readonly [key: string]: unknown } =>
  typeof value === 'object' && value !== null;

/** A failed request's response, when it got one. */
export function getErrorResponse(error: unknown): unknown {
  return isObject(error) ? error.response : undefined;
}

/** The HTTP status of a failed request's response, when it got one. */
export function getHttpStatus(error: unknown): number | undefined {
  const response = getErrorResponse(error);
  const status = isObject(response) ? response.status : undefined;
  return typeof status === 'number' ? status : undefined;
}

/** The first QCS error in a failed request's response. */
function getFirstQcsError(error: unknown): { readonly [key: string]: unknown } | undefined {
  const response = getErrorResponse(error);
  const data = isObject(response) ? response.data : undefined;
  const errors = isObject(data) ? data.errors : undefined;
  const firstError: unknown = Array.isArray(errors) ? errors[0] : undefined;
  return isObject(firstError) ? firstError : undefined;
}

/** The code of the first QCS error in a failed request's response, such as `QCS-0401-19`. */
export function getQcsErrorCode(error: unknown): string | undefined {
  const code = getFirstQcsError(error)?.code;
  return typeof code === 'string' ? code : undefined;
}

/**
 * The id QCS reports as already deleted (`QCS-0400-5`), such as a reminder another client dismissed.
 * Only the `id` request parameter counts, so a deleted related resource is not mistaken for the target.
 */
export function getQcsDeletedResourceId(error: unknown): string | undefined {
  const firstError = getFirstQcsError(error);
  if (firstError?.code !== 'QCS-0400-5') return undefined;
  const extData = isObject(firstError.extData) ? firstError.extData : undefined;
  if (extData?.parameterName !== 'id') return undefined;
  return typeof extData.parameterValue === 'string' ? extData.parameterValue : undefined;
}

/** Whether the request was blocked because the app is in demo mode. */
export function isDemoModeError(error: unknown): boolean {
  return isObject(error) && error.message === 'demoMode';
}

export function isHttpServerError(error: unknown): boolean {
  const status = getHttpStatus(error);
  return status !== undefined && status >= 500 && status < 600;
}
